// ✅ src/pages/Asset/AssetTransferApproval.jsx  (FULL FILE - UPDATED WITH localStorage + commonHeaders)
// Flow rules:
// - If approvalStatus is "Approved" -> Edit DISABLED
// - If approvalStatus is "Waiting for Approval" -> Edit ENABLED
// - Approve -> open details page in mode: "APPROVE"
// - Edit    -> open details page in mode: "RECEIVE" (view only)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaChevronDown, FaChevronUp, FaEdit } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/transferListing";
const RECEIVING_ROUTE = "/Asset/AssetTransferReceiving";

// ✅ date format helper
const formatDMY = (dateObj) => {
  if (!dateObj) return "";
  const d = new Date(dateObj);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const lastNDaysRange = (n = 7) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - n);
  return { from: formatDMY(from), to: formatDMY(to) };
};

const normalizeStatus = (v) => String(v ?? "").trim().toLowerCase();

const isApprovedStatus = (v) => {
  const st = normalizeStatus(v);
  return st === "approved" || st === "y" || st === "yes" || st === "true";
};

const isWaitingStatus = (v) => {
  const st = normalizeStatus(v);
  return (
    st === "waiting for approval" ||
    st === "waiting" ||
    st === "pending" ||
    st === "pending approval" ||
    st === "not approved" ||
    st === "unapproved"
  );
};

const AssetTransferApproval = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);

  const def = useMemo(() => lastNDaysRange(7), []);
  const [filters, setFilters] = useState({
    fromDate: def.from,
    toDate: def.to,
    fromLocation: "",
    toLocation: "",
  });

  const [branches, setBranches] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const getBranchId = (row) =>
    row?.fld_branchid ?? row?.branchid ?? row?.BranchId ?? "";

  const getBranchName = (row) =>
    row?.fld_branchName ??
    row?.branchName ??
    row?.BranchName ??
    String(getBranchId(row) || "");

  const getCol = (row, keys, fallback = "") => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return fallback;
  };

  const loadInit = async () => {
    try {
      setLoadingInit(true);

      const res = await axios.get(`${API_BASE}/init`, {
        params: { loginuserid: userId },
        headers: commonHeaders,
      });

      const data = res?.data?.data || {};
      setBranches(Array.isArray(data?.branches) ? data.branches : []);

      const initFrom = data?.from ? formatDMY(data.from) : "";
      const initTo = data?.defaultToDate ? formatDMY(data.defaultToDate) : "";

      setFilters((prev) => ({
        ...prev,
        fromDate: initFrom || prev.fromDate,
        toDate: initTo || prev.toDate,
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load init data.");
      setBranches([]);
    } finally {
      setLoadingInit(false);
    }
  };

  const fetchList = async () => {
    try {
      setLoadingList(true);
      setCurrentPage(1);

      const params = {
        pageid: "1",
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        fromLocation: filters.fromLocation,
        toLocation: filters.toLocation,
        loginuserid: userId,
        branchtype: branchType,
        branchid: branchId,
        finYearId,
        finYear,
      };

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: commonHeaders,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load list");
        setRecords([]);
        return;
      }

      setRecords(Array.isArray(res?.data?.data) ? res.data.data : []);
      toast.success("List loaded");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to load approval list"
      );
      setRecords([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.ceil(records.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = records.slice(startIndex, endIndex);

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 10;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= maxVisiblePages; i++) pages.push(i);
      pages.push("...");
    }
    return pages;
  };

  const canShowApprove = useMemo(() => {
    return String(branchType || "").toUpperCase() === "HO";
  }, [branchType]);

  const mapRowForReceiving = (row, sno) => {
    const transferId = getCol(
      row,
      ["TransferId", "fld_transferId", "transferId", "Id", "fld_id"],
      ""
    );
    const approvalStatus = getCol(
      row,
      [
        "ApprovalStatus",
        "fld_approvalStatus",
        "Status",
        "fld_status",
        "TransferStatus",
      ],
      ""
    );

    return {
      sno,
      transferId,
      approvalStatus,
      assetType: getCol(
        row,
        ["AssetType", "fld_assetType", "AssetTypeName", "assetType"],
        ""
      ),
      assetSubType: getCol(
        row,
        ["AssetSubType", "fld_assetSubType", "AssetSubTypeName", "assetSubType"],
        ""
      ),
      assetIdCodes: getCol(
        row,
        ["fld_idcodeno", "AssetIdCode", "fld_assetIdCode", "idCode", "assetCode"],
        ""
      ),
      fromBranch: getCol(
        row,
        ["FromBranch", "fld_fromBranch", "FromLocation", "fromBranch"],
        ""
      ),
      toBranch: getCol(
        row,
        ["ToBranch", "fld_toBranch", "ToLocation", "toBranch"],
        ""
      ),
      transferDate: getCol(
        row,
        ["fld_TransferDate", "TransferDate", "fld_transferDate", "Date", "fld_date"],
        ""
      ),
      transferReason: getCol(
        row,
        ["fld_TransferReason", "TransferReason", "fld_transferReason", "reason"],
        ""
      ),
    };
  };

  const goToReceiving = (row, sno, mode) => {
    const mapped = mapRowForReceiving(row, sno);

    sessionStorage.setItem("assetTransferSelected", JSON.stringify(mapped));

    navigate(RECEIVING_ROUTE, {
      state: {
        transferId: mapped.transferId,
        transfer: mapped,
        raw: row,
        mode, // ✅ fixed
      },
    });
  };

  const handleApprove = (row, sno) => {
    const flags = row?._flags || {};
    if (flags.canApprove === false) {
      toast.warn("You are not allowed to approve this transfer.");
      return;
    }
    goToReceiving(row, sno, "APPROVE");
  };

  const handleEdit = (row, sno, approvalStatus) => {
    if (!isWaitingStatus(approvalStatus)) {
      toast.info("Edit allowed only for Waiting for Approval.");
      return;
    }
    goToReceiving(row, sno, "RECEIVE");
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TRANSFER APPROVAL</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black border-gray-200"
          }`}
        >
          {/* Filters */}
          <div
            className={`mb-6 rounded-lg border ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div
              className={`flex justify-between items-center p-4 cursor-pointer ${
                isDark ? "hover:bg-gray-700/50" : "hover:bg-gray-100"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <h3 className={`${isDark ? "text-white" : "text-gray-900"} font-semibold`}>
                Search Filters
              </h3>
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
            </div>

            {showFilters && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      From Date
                    </label>
                    <input
                      type="text"
                      value={filters.fromDate}
                      onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                      placeholder="dd/mm/yyyy"
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      To Date
                    </label>
                    <input
                      type="text"
                      value={filters.toDate}
                      onChange={(e) => handleFilterChange("toDate", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                      placeholder="dd/mm/yyyy"
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      From Location
                    </label>
                    <select
                      value={filters.fromLocation}
                      onChange={(e) => handleFilterChange("fromLocation", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      } ${loadingInit ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      <option value="">{loadingInit ? "Loading..." : "Select"}</option>
                      {branches.map((b, idx) => (
                        <option key={`${getBranchId(b)}-${idx}`} value={getBranchId(b)}>
                          {getBranchName(b)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      To Location
                    </label>
                    <select
                      value={filters.toLocation}
                      onChange={(e) => handleFilterChange("toLocation", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      } ${loadingInit ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      <option value="">{loadingInit ? "Loading..." : "Select"}</option>
                      {branches.map((b, idx) => (
                        <option key={`${getBranchId(b)}-${idx}`} value={getBranchId(b)}>
                          {getBranchName(b)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={fetchList}
                    disabled={loadingList}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${
                      loadingList ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaEye />
                    {loadingList ? "Loading..." : "View"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* TABLE */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {records.length === 0 ? (
              <div className={`p-12 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div
                  className={`text-xl font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  No Records Found
                </div>
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No asset transfer records match the current filter criteria.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Asset Type
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Asset SubType
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Asset IdCodes
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        From Branch
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        To Branch
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Transfer Date
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Transfer Reason
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Approval Status
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">Action</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentRecords.map((r, idx) => {
                      const sno = startIndex + idx + 1;

                      const approvalStatus = getCol(r, [
                        "ApprovalStatus",
                        "fld_approvalStatus",
                        "Status",
                        "fld_status",
                        "TransferStatus",
                      ]);
                      const assetType = getCol(r, [
                        "AssetType",
                        "fld_assetType",
                        "AssetTypeName",
                        "assetType",
                      ]);
                      const assetSubType = getCol(r, [
                        "AssetSubType",
                        "fld_assetSubType",
                        "AssetSubTypeName",
                        "assetSubType",
                      ]);
                      const assetIdCodes = getCol(r, [
                        "fld_idcodeno",
                        "AssetIdCode",
                        "fld_assetIdCode",
                        "idCode",
                        "assetCode",
                      ]);
                      const fromBranch = getCol(r, [
                        "FromBranch",
                        "fld_fromBranch",
                        "FromLocation",
                        "fromBranch",
                      ]);
                      const toBranch = getCol(r, [
                        "ToBranch",
                        "fld_toBranch",
                        "ToLocation",
                        "toBranch",
                      ]);
                      const transferDate = getCol(r, [
                        "fld_TransferDate",
                        "TransferDate",
                        "fld_transferDate",
                        "Date",
                        "fld_date",
                      ]);
                      const transferReason = getCol(r, [
                        "fld_TransferReason",
                        "TransferReason",
                        "fld_transferReason",
                        "reason",
                      ]);

                      const flags = r?._flags || {};
                      const uiMode = r?._uiMode || {};

                      const allowApprove =
                        (uiMode.showApprove ?? canShowApprove) &&
                        (flags.canApprove ?? true);

                      const allowEditByStatus = isWaitingStatus(approvalStatus);
                      const allowEdit = (flags.canEdit ?? true) && allowEditByStatus;

                      return (
                        <tr
                          key={r?.id ?? r?.TransferId ?? `${sno}`}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                          }`}
                        >
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {sno}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(assetType || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(assetSubType || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(assetIdCodes || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(fromBranch || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(toBranch || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(transferDate || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(transferReason || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(approvalStatus || "")}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleEdit(r, sno, approvalStatus)}
                                disabled={!allowEdit}
                                className={`inline-flex items-center gap-2 px-4 py-1 rounded-lg text-sm font-semibold border ${
                                  isDark
                                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                                } ${!allowEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                                title={allowEdit ? "Edit" : "Edit disabled (Approved)"}
                              >
                                <FaEdit className="text-xs" />
                                Edit
                              </button>

                              {allowApprove ? (
                                <button
                                  onClick={() => handleApprove(r, sno)}
                                  className={`px-4 py-1 rounded-lg text-sm font-semibold ${buttonClass} ${hoverClass} text-white ${
                                    flags.canApprove === false ? "opacity-60 cursor-not-allowed" : ""
                                  }`}
                                  disabled={flags.canApprove === false}
                                  title="Approve"
                                >
                                  Approve
                                </button>
                              ) : (
                                <span className={`${isDark ? "text-gray-400" : "text-gray-500"} text-sm`}>
                                  -
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {records.length > 0 && (
            <div className="flex justify-start mt-6">
              <div className="flex gap-1">
                {renderPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => page !== "..." && setCurrentPage(page)}
                    disabled={page === "..."}
                    className={`px-3 py-2 text-sm font-medium rounded border ${
                      page === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : page === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTransferApproval;